import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Rocket,
  AppWindow,
  Layers,
  Server,
  Settings,
  ChevronLeft,
  ChevronRight,
  Users,
  UsersRound,
  Shield,
  FileText,
  Container,
  ShieldCheck,
  CheckCircle,
  Database,
  ToggleLeft,
  Image as ImageIcon,
  KeyRound,
  Monitor,
} from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui';
import { cn } from '@/utils/cn';
import { BrandMark } from '@/components/common';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: string;
  adminOnly?: boolean;
}

interface SidebarProps {
  onNavigate?: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps = {}) {
  const { t } = useTranslation();
  const { sidebarCollapsed, toggleSidebarCollapsed } = useUIStore();
  const { hasPermission, isAdminStcaria } = useAuthStore();

  const navItems: NavItem[] = [
    { to: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { to: '/deploys', label: t('nav.deploys'), icon: Rocket, permission: 'deploy:view' },
    { to: '/apps', label: t('nav.applications'), icon: AppWindow, permission: 'app:view' },
    { to: '/scopes', label: t('nav.scopes'), icon: Layers, permission: 'scope:view' },
    { to: '/config', label: t('nav.config'), icon: KeyRound, permission: 'scope:view' },
    { to: '/servers', label: t('nav.servers'), icon: Server, adminOnly: true },
    { to: '/infrastructure', label: t('nav.infrastructure'), icon: Monitor, permission: 'infrastructure:view' },
    { to: '/registries', label: t('nav.registries'), icon: Container, adminOnly: true },
    { to: '/images', label: t('nav.images'), icon: ImageIcon, permission: 'registry:view' },
    { to: '/approvals', label: t('nav.approvals'), icon: CheckCircle, permission: 'deploy:approve' },
    { to: '/policies', label: t('nav.policies'), icon: ShieldCheck, permission: 'settings:manage' },
    { to: '/backups', label: t('nav.backups'), icon: Database, adminOnly: true },
    { to: '/feature-flags', label: t('nav.featureFlags'), icon: ToggleLeft, permission: 'settings:view' },
    { to: '/teams', label: t('nav.teams'), icon: UsersRound, adminOnly: true },
    { to: '/users', label: t('nav.users'), icon: Users, permission: 'user:manage' },
    { to: '/roles', label: t('nav.roles'), icon: Shield, adminOnly: true },
    { to: '/audit', label: t('nav.audit'), icon: FileText, permission: 'audit:view' },
    { to: '/settings', label: t('nav.settings'), icon: Settings, adminOnly: true },
  ];

  const visibleItems = navItems.filter(
    (item) => (!item.adminOnly || isAdminStcaria()) && (!item.permission || hasPermission(item.permission))
  );

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r bg-background transition-all duration-300',
        sidebarCollapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex h-14 items-center gap-2 border-b px-4">
        {!sidebarCollapsed && (
          <>
            <BrandMark className="h-7 w-7 shrink-0" />
            <span className="text-lg font-semibold">{t('common.appName')}</span>
          </>
        )}
        <div className={cn('ml-auto', sidebarCollapsed && 'mx-auto')}>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebarCollapsed}
            className="h-8 w-8"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-2">
        {visibleItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground',
                sidebarCollapsed && 'justify-center px-2'
              )
            }
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {!sidebarCollapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="border-t p-2">
        <div
          className={cn(
            'flex items-center gap-2 rounded-md px-3 py-2 text-xs text-muted-foreground',
            sidebarCollapsed && 'justify-center'
          )}
        >
          {!sidebarCollapsed && <span>{t('common.appVersion')}</span>}
        </div>
      </div>
    </aside>
  );
}
